<template>
    <v-dialog
        persistent
        transition="dialog-top-transition"
        v-model="rotateDialog"
        content-class="rounded-xl"
        :max-width="$emerald.ifLang('ar', '650px', '1000px')"
    >
        <v-form ref="form">
            <v-card rounded="xl">
                <v-card-title class="pb-2">
                    {{$t('FinalAccountsPage.rotate-dialog.rotate-financial-file')}}
                </v-card-title>
                
                <!-- dialog content -->
                <v-card-text class="pb-0">
                    <bee-stepper
                        v-model="stepper"
                        :steps="5"
                        :stepsLabels="[
                            $t('FinalAccountsPage.rotate-dialog.stepper-validate'),
                            $t('FinalAccountsPage.rotate-dialog.stepper-financial-file'),
                            $t('FinalAccountsPage.rotate-dialog.stepper-move-trees'),
                            $t('FinalAccountsPage.rotate-dialog.stepper-rotate'),
                            $t('FinalAccountsPage.rotate-dialog.stepper-rotate-steps')
                        ]"
                        withLineBetween
                        stepersPosition="top"
                        :altLabels="false"
                        :editable="false"
                        :nonLinear="false"
                    >
                        <!-- validate -->
                        <template v-slot:stepperContent.step-1>
                            <!-- all-bonds-are-posted -->
                            <div class="text-center">
                                <v-icon size="22px" class="mb-1" :color="file_is_locked ? 'postedColor': 'redDarken1'">
                                    {{ file_is_locked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                                </v-icon>

                                <span class="ms-2" style="font-size: .95rem">
                                    {{
                                        file_is_locked
                                        ? $t('FinalAccountsPage.rotate-dialog.financial-file-is-locked')
                                        : $t('FinalAccountsPage.rotate-dialog.financial-file-is-not-locked')
                                    }}
                                </span>
                            </div>
                        </template>

                        <!-- fiscal-year -->
                        <template v-slot:stepperContent.step-2>
                            <v-row class="mt-1">
                                <!-- new Financial File Name -->
                                <v-col :md="$emerald.ifLang('ar', '12', '6')" cols="12" class="py-2">
                                    <bee-text-field
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.new-financial-file-name')"
                                        v-model="newFinancialFileName"
                                        :rules="[rules.required]"
                                        @keydown-enter-prevent="increaseStepper"
                                    ></bee-text-field>
                                </v-col>

                                <!-- year Name -->
                                <v-col sm="6" cols="12" class="py-2">
                                    <bee-text-field
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.fiscal-year-name')"
                                        v-model="yearName"
                                        :rules="[rules.required]"
                                        @keydown-enter-prevent="increaseStepper"
                                    ></bee-text-field>
                                </v-col>

                                <!-- default-language -->
                                <v-col :md="$emerald.ifLang('ar', '6', '4')" sm="6" cols="12" class="py-2">
                                    <bee-autocomplete
                                        v-model="defaultLanguage"
                                        :label="$t('inputs.default-language')"
                                        :items="availableLangs"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[rules.language]"
                                        context="financial_file"
                                        :action-name="actions.CREATE"
                                    ></bee-autocomplete>
                                </v-col>

                                <!-- start year -->
                                <v-col :md="$emerald.ifLang('ar', '6', '4')" sm="6" cols="12" class="py-2">
                                    <bee-date-picker
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.start-fiscal-year')"
                                        v-model="startDate"
                                        :rules="[rules.required]"
                                        @keydown-enter-prevent="increaseStepper"
                                    ></bee-date-picker>
                                </v-col>

                                <!-- end year -->
                                <v-col :md="$emerald.ifLang('ar', '6', '4')" sm="6" cols="12" class="py-2">
                                    <bee-date-picker
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.end-fiscal-year')"
                                        v-model="endDate"
                                        :rules="[rules.required]"
                                        @keydown-enter-prevent="increaseStepper"
                                    ></bee-date-picker>
                                </v-col>
                            </v-row>
                        </template>

                        <!-- trees -->
                        <template v-slot:stepperContent.step-3>
                            <v-row class="mt-1">
                                <!-- move Accounts Tree -->
                                <v-col cols="12" class="py-2">
                                    <bee-checkbox
                                        v-model="moveAccountsTree"
                                        :disabled="true"
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.move-accounts-tree')"
                                        class="ma-0"
                                    ></bee-checkbox>
                                </v-col>

                                
                                <template>
                                    <!-- First term -->
                                    <v-col cols="12" class="py-2 pb-0">
                                        <span class="d-block mb-1 text--primary" style="font-size: 1rem">
                                            {{$t('FinalAccountsPage.rotate-dialog.first-term-account.text')}}
                                        </span>
                                    </v-col>

                                    <!-- parent Account Of First Term AccountId -->
                                    <v-col :md="$emerald.ifLang('ar', '12', '4')" cols="12" class="py-2">
                                        <bee-account-autocomplete
                                            context="financial_file"
                                            :action-name="actions.EDIT"
                                            :label="$t('FinalAccountsPage.rotate-dialog.first-term-account.account-parent')"
                                            :hideDetails="true"
                                            v-model="parentAccountOfFirstTermAccountId"
                                            :items="configs.accounts.filter(c => c.type == 1)"
                                            :disabled="rotateStep >= rotateSteps.moveAccountsTree"
                                            @change="setFirstTermAccountNo"
                                        ></bee-account-autocomplete>
                                    </v-col>

                                    <!-- first Term Account Name -->
                                    <v-col :md="$emerald.ifLang('ar', '6', '4')" sm="6" cols="12" class="py-2">
                                        <bee-text-field
                                            context="financial_file"
                                            :action-name="actions.EDIT"
                                            :label="$t('FinalAccountsPage.rotate-dialog.first-term-account.account-name')"
                                            v-model="firstTermAccountName"
                                            :rules="[rules.required]"
                                            :disabled="rotateStep >= rotateSteps.moveAccountsTree"
                                            @keydown-enter-prevent="increaseStepper"
                                        ></bee-text-field>
                                    </v-col>

                                    <!-- first Term Account No -->
                                    <v-col :md="$emerald.ifLang('ar', '6', '4')" sm="6" cols="12" class="py-2">
                                        <bee-accounting-no-field
                                            context="financial_file"
                                            :action-name="actions.EDIT"
                                            :label="$t('FinalAccountsPage.rotate-dialog.first-term-account.account-no')"
                                            v-model="firstTermAccountNo"
                                            :rules="[rules.required, notSameNoParent]"
                                            :with-balance="true"
                                            :prefix="firstTermAccountPrefixNo"
                                            :disabled="rotateStep >= rotateSteps.moveAccountsTree"
                                            @keydown-enter-prevent="increaseStepper"
                                        ></bee-accounting-no-field>
                                    </v-col>
                                </template>

                                <v-col cols="12" class="py-2">
                                    <bee-checkbox
                                        context="financial_file"
                                        :action-name="actions.EDIT"
                                        :label="$t('FinalAccountsPage.rotate-dialog.move-materials-trees')"
                                        v-model="moveMaterialsTree"
                                        :disabled="rotateStep >= rotateSteps.moveItemTrees"
                                        class="ma-0 mb-3"
                                    ></bee-checkbox>
                                </v-col>
                            </v-row>
                        </template>

                        <!-- rotate -->
                        <template v-slot:stepperContent.step-4>
                            <div v-html="$t('FinalAccountsPage.rotate-dialog.details')" class="px-5">
                            </div>
                        </template>

                        <!-- rotate steps -->
                        <template v-slot:stepperContent.step-5>
                            <!-- all-bonds-are-posted -->
                            <v-row
                                v-for="(state, index) in progressStates"
                                :key="index"
                            	class="align-center"
                            >
                                <v-col cols="4" class="py-1">
                                    <span style="font-size: .95rem">{{state.text}}</span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="state.isDone || loading"
                                        reverse
                                        :height="state.isDone ? 2 : 3"
                                        :color="state.isDone === true ? 'success-color' : 'grey darken-3'"
                                        stream
                                        :buffer-value="state.isDone ? 100 : 0"
                                        :value="state.isDone ? 100 : 0"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!state.isDone && loading"
                                        color="primary"
                                        size="21"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else-if="state.isDone"
                                        :color="
                                            state.isDone !== -1
                                                ? state.isDone ? 'success-color': 'danger-color'
                                                : 'grey darken-3'
                                        "
                                        size="27"
                                    >
                                        {{
                                            state.isDone !== -1
                                                ? state.isDone
                                                    ? 'mdi-check-circle-outline'
                                                    : 'mdi-close-circle-outline'
                                                : 'mdi-minus-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>
                        </template>
                    </bee-stepper>
                </v-card-text>
                <v-card-actions class="px-5">
                    <v-btn
                        color="grey darken-1"
                        text
                        :disabled="
                            stepper < 2 ||
                            (stepper === 3 && rotateStep >= rotateSteps.createNewFinancialFile) ||
                            (stepper === 4 && (rotateStep >= rotateSteps.moveItemTrees || isMoveItemsTreeDeprecated)) ||
                            loading
                        "
                        @click="stepper--"
                    >
                        {{$t('FinalAccountsPage.previous')}}
                    </v-btn>
                    <v-btn
                        color="greenDarken1"
                        text
                        :disabled="btnDisable || loading || getProgressLoading"
                        :loading="loading || getProgressLoading"
                        @click="stepper !== 5 ? increaseStepper() : (() => { holdPageReload(); rotateByStep(); })()"
                    >
                        {{
                            stepper === 5 || (rotateStep > 0 && stepper === 1)
                                ? rotateStep > rotateSteps.createNewFinancialFile && rotateStep !== rotateSteps.addAccessPermissions
                                    ? $t('FinalAccountsPage.resume')
                                    : $t('FinalAccountsPage.rotate')
                                : $t('FinalAccountsPage.next')
                        }}
                    </v-btn>
                    <v-btn color="redDarken1" :disabled="loading" text @click="rotateDialog = false; stepper = 1">
                        {{$t('FinalAccountsPage.cancel')}}
                    </v-btn>
                    <v-spacer></v-spacer>

                    <v-btn
                    	v-if="rotateStep > 0 && rotateStep < 7 && isAdmin && stepper === 1"
                    	color="preview-color"
                    	:disabled="loading || getProgressLoading"
                    	text
                    	@click="initNewFinancialFileData(); resetStepsProgress(); rotateStep = 0; stepper++"
                    >
                        {{$t('FinalAccountsPage.re-rotate')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
        <v-snackbar color="teal" v-model="snackbar">
            {{message}}

            <template v-slot:action="{ attrs }">
                <v-btn
                	color="red"
                	icon
                	v-bind="attrs"
                	@click="snackbar = false"
                >
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </v-dialog>
</template>

<script>
import BeeStepper from '../../../custom-components/toolkits/readonly/BeeStepper.vue'
import { mapState } from 'vuex';
import rules from '../../../../validation'
import availableLangs from '../../../../settings/constants/availableLangs'
import actions from '../../../../permissions/actions-names'
import { accountSuggestionType } from '@/helpers/enums';
// import eventBus from '../../../../event-bus'
// import ls from '../../../../settings/localStorageExtension'
export default {
    props: {
        value: { type: Boolean }
    },

    components: {
        BeeStepper
    },

    data() {
        return {
            snackbar: false,
            message: null,

            stepper: 1,

            rotateStep: 0,
            invokedRotateStep: null,
            isMoveItemsTreeDeprecated: null,
            
            getProgressLoading: false,
            progressInterval: null,
            progressStates: [
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.create-new-financial-file'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.move-accounts-tree'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.move-custom-statements'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.move-item-trees'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.create-opening-accounting-bond'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.create-opening-items-journal'),
                    isDone: false
                },
                {
                    text: this.$t('FinalAccountsPage.rotate-dialog.steps.init-access-permissions'),
                    isDone: false
                },
            ],
            rotateSteps: {
                createNewFinancialFile: 1,
                moveAccountsTree: 2,
                moveCustomStatements: 3,
                moveItemTrees: 4,
                createOpeningAccountingBond: 5,
                createOpeningItemsJournal: 6,
                addAccessPermissions: 7,
            },
            
			newFinancialFileName: null,
            yearName: null,
            startDate: null,
            endDate: null,
            defaultLanguage: null,

			moveAccountsTree: true,
			moveMaterialsTree: true,
			parentAccountOfFirstTermAccountId: null,
			firstTermAccountName: null,
            firstTermAccountNo: null,
            
            loading: false,
            ...rules,
            availableLangs,
            actions
        }
    },

    watch: {
        value(newVal) {
            if (newVal) {
                this.defaultLanguage =
                    this.configs.financial_file.default_language
                    ? this.configs.financial_file.default_language
                    : 1;
                
                if (!this.firstTermAccountName) {
                    this.$store.dispatch('accounts/getSuggestNameForEndingAccountingPeriod', {
                        type: accountSuggestionType.openingInventory
                    })
                    .then((data) => {
                        this.firstTermAccountName = data;
                    })
                }
                
                if (this.currentFinancialFile.rotated_financial_file_id) {
                    this.getProgressLoading = true;
                    this.$store.dispatch('financialFiles/fetchRotateProgress', {
                        financialFileId: this.configs.financial_file.id,
                        rotateFileId: this.currentFinancialFile.rotated_financial_file_id,
                    }).then((data) => {
                        if (data.step !== this.rotateSteps.addAccessPermissions && data.step !== 0) {
                            this.rotateStep = data.step;
                            this.invokedRotateStep = data.invokedStep;
                            this.isMoveItemsTreeDeprecated = data.isMoveItemsTreeDeprecated;
                            this.moveMaterialsTree = !data.isMoveItemsTreeDeprecated;

                            for (let i = 0; i < this.rotateStep; i++) {
                                this.progressStates[i].isDone = true;
                            }
                            
                            if (this.isMoveItemsTreeDeprecated) {
                                this.progressStates[this.rotateSteps.moveItemTrees - 1].isDone = -1; 
                                this.progressStates[this.rotateSteps.createOpeningItemsJournal - 1].isDone = -1;
                            }
                        } else {
                            this.initNewFinancialFileData();
                        }
                    }).finally(() => {
                        this.getProgressLoading = false;
                    })
                } else {
                    this.initNewFinancialFileData();
                }
            }
        }
    },

    computed: {
        ...mapState("configs", ["configs"]),
        ...mapState({
            isAdmin: state => state.auth.self.is_admin,
            currentFinancialFile: state => state.financialFiles.current_financial_file,
        }),

        file_is_locked() {
			return this.configs?.financial_file?.is_locked;
		},

        rotateDialog: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit('input', newValue);
            }
        },

        firstTermAccountPrefixNo(){
			return this.configs.accounts
				.find(c => c.id == this.parentAccountOfFirstTermAccountId)
				?.accounting_no.toString() ?? null
        },

        btnDisable() {
            return this.rotateStep < this.rotateSteps.moveAccountsTree && (
                !this.file_is_locked ||
                (this.stepper === 2 && (!this.newFinancialFileName || !this.yearName || !this.defaultLanguage))
                || (this.stepper === 3 && (!this.parentAccountOfFirstTermAccountId || !this.firstTermAccountName || !this.firstTermAccountNo))
            )
        }
    },

    methods: {
        /* rotateFinancialFile(){
            this.loading = true
			this.$store.dispatch('financialFiles/rotate', {
                financialFileId: this.configs.financial_file.id,
                rotated_financial_file_dto: {
                    name: this.newFinancialFileName,
                    year: this.yearName,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    default_language: this.defaultLanguage
                },
				open_inventory_account: {
					parent_id: this.parentAccountOfFirstTermAccountId,
					name: this.firstTermAccountName,
					inner_no: this.firstTermAccountNo
				},
				with_accounts: this.moveAccountsTree,
				with_materials: this.moveMaterialsTree
			})
			.then((data) => {
                this.snackbar = true;
                this.message = this.$t('FinalAccountsPage.rotate-dialog.wait-please');

                ls.setLocalFinancialFileId(data);
                eventBus.$emit("financial-file-id-changed");

                this.$store.dispatch('configs/invalidateCache')
                .finally(() => {
                    this.loading = false
                    this.$store.dispatch("auth/logout")
                    this.$router.replace({ name: "login" })
                    .then(() => {
                        window.navigation.reload();
                    });
                })
			})
        }, */

        initNewFinancialFileData () {
            this.newFinancialFileName = this.currentFinancialFile.company_name;
            this.yearName = String(new Date().getFullYear() + 1);
            this.startDate = `${this.yearName}-01-01`;
            this.endDate = `${this.yearName}-12-31`;
        },

        resetStepsProgress() {
            for (let i = 0; i < this.progressStates.length; i++) {
                this.progressStates[i].isDone = false;
            }
        },
        
        holdPageReload() {
            window.onbeforeunload = (e) => {
                e.preventDefault();
                return (e.returnValue = "Revolving is in progress, are you sure you want to reload the page");
            }
        },

        rotateByStep(isError = false) {
            const progressIntervalHandler = (rotateFileId, succeededStep) => {
                this.progressInterval = setInterval(() => {
                    this.$store.dispatch('financialFiles/fetchRotateProgress', {
                        financialFileId: this.configs.financial_file.id,
                        rotateFileId: rotateFileId
                    }).then((data) => {
                        if (data.step === succeededStep) {
                            clearInterval(this.progressInterval);
                            this.progressStates[succeededStep - 1].isDone = true;
                            if (this.invokedRotateStep) {
                                this.rotateStep = this.invokedRotateStep;
                                this.invokedRotateStep = null;
                            }
                            this.rotateByStep();
                        } else {
                            this.message = this.progressStates[succeededStep - 1].text + ' ' +
                                this.$t('FinalAccountsPage.rotate-dialog.in-progress')
                            this.snackbar = true;
                        }
                    })
                }, 1000 * 45);
            }

            // if there already step in progress then just watch the progress interval
            if (this.invokedRotateStep && this.rotateStep !== 0) {
                this.loading = true;
                progressIntervalHandler(
                    this.currentFinancialFile.rotated_financial_file_id,
                    this.invokedRotateStep
                );
                return;
            }

            // if rotate steps don't end and there is no error 
            if (this.rotateStep < this.rotateSteps.addAccessPermissions && !isError) {
                this.loading = true;
                let nextStep = ++this.rotateStep;
                
                //  if moveMaterialsTree is deprecated then it shouldn't create createOpeningItemsJournal
                if (!this.moveMaterialsTree && nextStep === this.rotateSteps.createOpeningItemsJournal) {
                    this.progressStates[nextStep - 1].isDone = -1;
                    nextStep = ++this.rotateStep;
                }

                this.$store.dispatch('financialFiles/rotateWithStep', {
                    step: nextStep,

                    // step 1 data (createNewFinancialFile step)
                    financialFileId: this.configs.financial_file.id,
                    name: this.newFinancialFileName,
                    year: this.yearName,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    defaultLanguage: this.defaultLanguage,

                    // step 2 data (moveAccountsTree step)
                    createOpeningInventoryAccount: true,
                    accountName: this.firstTermAccountName,
                    parentAccountId: this.parentAccountOfFirstTermAccountId,
                    innerAccountingNo: this.firstTermAccountNo,

                    // step 4 data (moveItemTrees step)
                    ignoreNegativeItemsCount: false,
                    isDeprecated: !this.moveMaterialsTree
                }).then((rotatedCurrentFinancialFileId) => {
                    // if the step is creating new financial file then don't watch progress
                    if (nextStep === this.rotateSteps.createNewFinancialFile) {
                        this.progressStates[nextStep - 1].isDone = true; 
                        
                        this.rotateByStep();
                        return;
                    }

                    // if move materials Tree skipped then don't watch progress and update states
                    if (!this.moveMaterialsTree &&  nextStep === this.rotateSteps.moveItemTrees){
                        this.progressStates[nextStep - 1].isDone = -1;

                        this.rotateByStep();
                        return;
                    }

                    // watch progress
                    progressIntervalHandler(
                        rotatedCurrentFinancialFileId || this.currentFinancialFile.rotated_financial_file_id,
                        nextStep
                    )
                }).catch(() => {
                    this.rotateByStep(true)
                })
            } else if (isError) {
                this.loading = false;
                return
            } else {
                window.onbeforeunload = null;
                this.message = this.$t('messages.revolve-succeeded');
                this.snackbar = true;
                this.loading = false;
                this.$store.dispatch("auth/logout");
                setTimeout(() => {
                    this.$router.replace({ name: "login" }).then(() => {
                        this.$router.go(0);
                    });
                }, 1500);
            }
        },

        setFirstTermAccountNo(){
            if (this.parentAccountOfFirstTermAccountId){
                this.$store.dispatch("accounts/GenerateNewAccountNo", this.parentAccountOfFirstTermAccountId)
                .then(response => {
                    this.firstTermAccountNo = response;
                })
            }
            else {
                this.firstTermAccountNo = null;
            }
        },
        
        notSameNoParent(val) {
            return !this.configs.accounts.filter(c => c.type == 1).map(c => c.accounting_no).includes(val) || this.$t('FinalAccountsPage.rotate-dialog.same-parent-no')
        },

        increaseStepper() {
            if (!this.btnDisable){
                let tempStep;

                if (
                    [
                        this.rotateSteps.createNewFinancialFile,
                        this.rotateSteps.moveAccountsTree,
                        this.rotateSteps.moveCustomStatements,
                    ].includes(this.rotateStep)
                    && !this.isMoveItemsTreeDeprecated
                ) {
                    tempStep = 3;
                } else if (
                    [
                        this.rotateSteps.moveItemTrees,
                        this.rotateSteps.createOpeningAccountingBond,
                        this.rotateSteps.createOpeningItemsJournal,
                        this.rotateSteps.addAccessPermissions
                    ].includes(this.rotateStep)
                ) {
                    tempStep = 4;
                }
                if (this.stepper < tempStep) {
                    this.stepper = tempStep;
                } else {
                    this.stepper++;
                }
            }
        }
    }
}
</script>

<style>

</style>